/**
 * history_epoch — when does the picture's history stop describing the picture? (shader gaps program, 2026-09-29; the
 * owner's report §4.3 / §14.6 "a common history epoch for camera cuts, teleportation, scene changes").
 *
 * TAAU / TRAA blend each new frame into a history of the previous ones, and three's nodes re-seed that history only on a
 * RESIZE. A teleport, a world entry, a respawn or a cockpit switch therefore smeared the old view into the new one for a
 * dozen frames, and the cut's own velocity (the whole jump, in one frame) streaked the motion blur across the screen.
 * The engine's `PostProcessingPipeline.resetHistory(reason)` re-seeds; this module decides WHEN — without touching each
 * of the many teleport paths (debug_teleport, the reconcile hard snap, placeFeet, enterWorld, the probe runner, …):
 *
 *   - A CAMERA CUT is a jump the camera's own recent motion cannot explain: a one-frame displacement over
 *     `max(CUT_MIN_M, CUT_FREE_MPS × dt, CUT_SPEED_K × the eased speed × dt + CUT_SLACK_M)` — velocity-relative, so a hovercraft at
 *     190 m/s under a one-second SwiftShader frame is not a cut, and a 500 m debug teleport from a walk is — or a
 *     rotation over `CUT_TURN_RAD` in one frame from a camera that was barely turning (a snap, not a flick in progress).
 *   - EVENTS the module is told about (world entered / swapped / cleared, space flight entered or left).
 *
 * The camera position is ABSOLUTE (render position − the FP64 rebase shift): a pure origin rebase moves the camera and
 * every object together, the velocities stay consistent, and it is NOT a cut. In space the sector keeps its own floating
 * origin, so the position test is off there (`spatial = false`) and the events carry the cuts. Pure — no three, no DOM.
 */

/** Below this a one-frame jump is never a cut (m) — a vehicle entry or a camera-mode snap is left to the resolve. */
export const CUT_MIN_M = 40;
/** … nor anything a craft could cover from REST in one frame (m/s × dt): at one-second SwiftShader frames a ride's first
 *  frame moves ~190 m before any speed has been eased in. A frame-time allowance, not a speed limit. */
export const CUT_FREE_MPS = 250;
/** A jump must exceed this many times the eased speed × dt … */
export const CUT_SPEED_K = 4;
/** … plus this slack (m). */
export const CUT_SLACK_M = 10;
/** A one-frame turn above this (rad, 60°) from a camera turning slower than `CUT_TURN_CALM` (rad/s) is a snap. */
export const CUT_TURN_RAD = Math.PI / 3;
export const CUT_TURN_CALM = 1.5;
/** Easing of the speed estimate (per second). */
const SPEED_EASE = 4;

export interface CameraSample {
  /** Absolute camera position (m). */
  x: number; y: number; z: number;
  /** Camera orientation (unit quaternion). */
  qx: number; qy: number; qz: number; qw: number;
  /** Frame time (s). */
  dt: number;
  /** false where the position is not in one continuous frame (the space sector) — only the turn test runs. */
  spatial?: boolean;
}

export interface CutVerdict { cut: boolean; reason: string | null; jumpM: number; turnRad: number }

/** The angle between two unit quaternions (rad). */
export function quatAngle(ax: number, ay: number, az: number, aw: number, bx: number, by: number, bz: number, bw: number): number {
  const d = Math.min(1, Math.abs(ax * bx + ay * by + az * bz + aw * bw));
  return 2 * Math.acos(d);
}

export class CutDetector {
  private prev: CameraSample | null = null;
  private speed = 0; // eased m/s
  private turnRate = 0; // eased rad/s
  cuts = 0;

  /** Forget the last sample (the next one starts a new track — after a world swap, say). */
  forget(): void { this.prev = null; this.speed = 0; this.turnRate = 0; }

  /** Feed one frame's camera; returns whether it is a cut. */
  sample(s: CameraSample): CutVerdict {
    const p = this.prev;
    this.prev = { ...s };
    if (!p || !(s.dt > 0) || !Number.isFinite(s.x + s.y + s.z)) return { cut: false, reason: null, jumpM: 0, turnRad: 0 };
    const spatial = s.spatial !== false && p.spatial !== false;
    const jump = spatial ? Math.hypot(s.x - p.x, s.y - p.y, s.z - p.z) : 0;
    const turn = quatAngle(p.qx, p.qy, p.qz, p.qw, s.qx, s.qy, s.qz, s.qw);
    const dt = Math.min(s.dt, 2);
    let reason: string | null = null;
    if (spatial && jump > Math.max(CUT_MIN_M, CUT_FREE_MPS * dt, CUT_SPEED_K * this.speed * dt + CUT_SLACK_M)) reason = `jump ${jump.toFixed(1)} m`;
    else if (turn > CUT_TURN_RAD && this.turnRate < CUT_TURN_CALM) reason = `snap ${(turn * 180 / Math.PI).toFixed(0)}°`;
    const k = 1 - Math.exp(-SPEED_EASE * dt);
    if (reason) {
      this.cuts++;
      this.speed = 0; this.turnRate = 0; // a cut starts a new track: its jump is not a speed
    } else {
      this.speed += (jump / dt - this.speed) * k;
      this.turnRate += (turn / dt - this.turnRate) * k;
    }
    return { cut: reason !== null, reason, jumpM: jump, turnRad: turn };
  }

  /** The eased speed (m/s) and turn rate (rad/s) — the probe lever. */
  state(): { speed: number; turnRate: number; cuts: number } { return { speed: this.speed, turnRate: this.turnRate, cuts: this.cuts }; }
}

/** The Atlas bus events that are cuts by definition (world transitions; PostModule adds the space-flight flag flips). */
export const CUT_EVENTS = ['atlas:world-entered', 'atlas:world-swapping', 'atlas:world-cleared', 'atlas:world-adopted'] as const;
